import { useState, useCallback, useRef, useEffect } from "react";

function useAuth() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem('id_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data?.type === 'auth_callback' && e.data.id_token) {
        sessionStorage.setItem('id_token', e.data.id_token);
        setToken(e.data.id_token);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(u => { setUser(u); setLoading(false); })
      .catch(() => { sessionStorage.removeItem('id_token'); setToken(null); setLoading(false); });
  }, [token]);

  const login = () => window.open('/auth/login', 'login', 'width=500,height=600');
  const logout = () => { sessionStorage.removeItem('id_token'); setToken(null); setUser(null); };
  return { user, token, loading, login, logout };
}

function useApi(token) {
  const sendMessage = useCallback(async (message, conversationId) => {
    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ message, conversationId }),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }, [token]);

  const uploadFiles = useCallback(async (files, message, conversationId) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('message', message || '');
    formData.append('conversationId', conversationId || '');
    const response = await fetch('/api/upload', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) throw new Error(`Upload Error: ${response.status}`);
    return response.json();
  }, [token]);

  return { sendMessage, uploadFiles };
}

const QUICK_TASKS = [
  { label: '📄 E-Rechnung', prompt: 'Ich möchte eine E-Rechnung erstellen' },
  { label: '💼 Job anlegen', prompt: 'Ich möchte einen neuen Job anlegen' },
  { label: '🔍 Jobs suchen', prompt: 'Zeig mir alle aktiven Jobs' },
  { label: '📊 Gehalt aufteilen', prompt: 'Ich möchte Gehaltsabrechnungen aufteilen' },
];

function LoginScreen({ onLogin }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f1923 0%, #1a2a3a 50%, #0d2137 100%)',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <div style={{
        width: 400, background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16,
        padding: '48px 40px', backdropFilter: 'blur(20px)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', fontSize: 24,
          }}>⚡</div>
          <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 600, margin: '0 0 8px', letterSpacing: '-0.02em' }}>Office Bot</h1>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>mund-consulting.de</p>
        </div>
        <button onClick={onLogin} style={{
          width: '100%', padding: '14px', borderRadius: 10, border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
          color: '#fff', fontSize: 15, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}>
          <span>🔐</span> Mit Microsoft anmelden
        </button>
      </div>
    </div>
  );
}

function FileBadge({ file, onRemove }) {
  const ext = file.name.split('.').pop().toUpperCase();
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'rgba(45,127,249,0.15)', border: '1px solid rgba(45,127,249,0.3)',
      borderRadius: 6, padding: '4px 8px', fontSize: 12, color: '#7eb8ff',
    }}>
      <span style={{ fontWeight: 700, fontSize: 10, background: 'rgba(45,127,249,0.3)', borderRadius: 3, padding: '1px 4px' }}>{ext}</span>
      <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
      <button onClick={() => onRemove(file)} style={{
        background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)',
        padding: 0, fontSize: 14, lineHeight: 1, marginLeft: 2,
      }}>×</button>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{
      display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 16, gap: 10, alignItems: 'flex-start',
    }}>
      {!isUser && (
        <div style={{
          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
          background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
        }}>⚡</div>
      )}
      <div style={{ maxWidth: '72%' }}>
        <div style={{
          padding: '12px 16px', borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
          background: isUser ? 'linear-gradient(135deg, #2d7ff9, #1a6fe8)' : 'rgba(255,255,255,0.06)',
          color: '#fff', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap',
          border: isUser ? 'none' : '1px solid rgba(255,255,255,0.08)',
        }}>
          {msg.content}
        </div>
        {msg.files && msg.files.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {msg.files.map((f, i) => (
              <span key={i} style={{
                fontSize: 11, color: 'rgba(255,255,255,0.4)',
                background: 'rgba(255,255,255,0.06)', borderRadius: 4, padding: '2px 6px',
              }}>📎 {f.name}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
        background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
      }}>⚡</div>
      <div style={{
        padding: '12px 16px', borderRadius: '4px 16px 16px 16px',
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', gap: 4, alignItems: 'center',
      }}>
        {[0,1,2].map(i => (
          <div key={i} style={{
            width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.4)',
            animation: 'bounce 1.2s infinite', animationDelay: `${i * 0.2}s`,
          }}/>
        ))}
      </div>
    </div>
  );
}

function ChatApp({ user, onLogout, token }) {
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: `Hallo ${user.name.split(' ')[0]}! Wie kann ich dir helfen?`,
  }]);
  const [input, setInput] = useState('');
  const [files, setFiles] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [conversationId] = useState(() => Math.random().toString(36).slice(2));
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const { sendMessage, uploadFiles } = useApi(token);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleBotResponse = (response) => {
    const text = response.output || response.message || JSON.stringify(response);
    setMessages(prev => [...prev, { role: 'assistant', content: text }]);
    setIsTyping(false);
  };

  const handleError = (error) => {
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `Es ist ein Fehler aufgetreten: ${error.message}\n\nBitte versuchen Sie es erneut.`,
    }]);
    setIsTyping(false);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text && files.length === 0) return;
    const userMsg = {
      role: 'user',
      content: text || `${files.length} Datei(en) hochgeladen`,
      files: files.map(f => ({ name: f.name, size: f.size })),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    const currentFiles = [...files];
    setFiles([]);
    setIsTyping(true);
    try {
      const response = currentFiles.length > 0
        ? await uploadFiles(currentFiles, text, conversationId)
        : await sendMessage(text, conversationId);
      handleBotResponse(response);
    } catch (error) {
      handleError(error);
    }
  };

  const handleFileSelect = (newFiles) => {
    const accepted = Array.from(newFiles).filter(f =>
      ['.pdf','.xml','.csv','.xlsx','.png','.jpg','.jpeg'].some(ext => f.name.toLowerCase().endsWith(ext))
    );
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      return [...prev, ...accepted.filter(f => !existing.has(f.name))];
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const initials = user.name?.split(' ').map(n => n[0]?.toUpperCase()).join('').slice(0, 2) || 'U';

  return (
    <div style={{
      display: 'flex', height: '100vh', background: '#0f1923',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: '#fff',
    }}>
      <style>{`
        @keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:2px}
        textarea{resize:none;outline:none}
      `}</style>
      <div style={{
        width: 240, background: 'rgba(255,255,255,0.02)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', flexDirection: 'column', padding: '20px 14px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
          }}>⚡</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Office Bot</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>mund-consulting.de</div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
          Schnellaufgaben
        </div>
        {QUICK_TASKS.map((task, i) => (
          <button key={i} onClick={() => setInput(task.prompt)}
            style={{
              width: '100%', textAlign: 'left', padding: '9px 10px', borderRadius: 7, border: 'none',
              background: 'transparent', color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
              fontSize: 13, marginBottom: 2,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
          >{task.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #2d7ff9, #18bfff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
          }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
          </div>
          <button onClick={onLogout} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: 16, padding: 4 }}>↪</button>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 0' }}>
          {messages.map((msg, i) => <MessageBubble key={i} msg={msg} />)}
          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} style={{ height: 24 }} />
        </div>
        {files.length > 0 && (
          <div style={{ padding: '8px 24px 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {files.map((f, i) => <FileBadge key={i} file={f} onRemove={(file) => setFiles(prev => prev.filter(x => x.name !== file.name))} />)}
          </div>
        )}
        <div style={{ padding: '12px 24px 24px' }}>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              background: dragOver ? 'rgba(45,127,249,0.1)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${dragOver ? 'rgba(45,127,249,0.5)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 14, padding: '12px 14px', transition: 'all 0.2s',
            }}
          >
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nachricht eingeben oder Datei(en) hochladen..."
              rows={2}
              style={{ width: '100%', background: 'none', border: 'none', color: '#fff', fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input ref={fileInputRef} type="file" multiple accept=".pdf,.xml,.csv,.xlsx,.png,.jpg,.jpeg" style={{ display: 'none' }}
                  onChange={e => { handleFileSelect(e.target.files); e.target.value = ''; }} />
                <button onClick={() => fileInputRef.current?.click()} style={{
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.5)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  📎 {files.length > 0 ? `${files.length} Datei(en)` : 'Dateien'}
                </button>
              </div>
              <button onClick={handleSend} disabled={!input.trim() && files.length === 0} style={{
                background: (!input.trim() && files.length === 0) ? 'rgba(45,127,249,0.3)' : 'linear-gradient(135deg, #2d7ff9, #18bfff)',
                border: 'none', borderRadius: 9, padding: '8px 18px',
                color: '#fff', cursor: (!input.trim() && files.length === 0) ? 'default' : 'pointer',
                fontSize: 14, fontWeight: 600,
              }}>Senden ↑</button>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>
            Mehrere Dateien gleichzeitig möglich · PDF, XML, CSV, XLSX, PNG, JPG
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { user, token, loading, login, logout } = useAuth();
  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0f1923', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>Laden...</div>
    </div>
  );
  if (!user) return <LoginScreen onLogin={login} />;
  return <ChatApp user={user} token={token} onLogout={logout} />;
}
